import React, { useState } from 'react';
import { ChevronDown, ChevronRight, CheckCircle, Loader, HelpCircle } from 'lucide-react';
import { cn } from '../../utils/cn';

export const CollapsibleSection = ({ title, index, children, questionStatuses, startIdx, endIdx }) => {
    const [isOpen, setIsOpen] = useState(index === 0);

    // Calculate completion stats for this section
    const total = endIdx - startIdx;
    let completed = 0;
    let processing = 0;
    for (let i = startIdx; i < endIdx; i++) {
        const status = questionStatuses[`Question${i + 1}`];
        if (status === 'completed' || status === 'success') completed++;
        else if (status === 'processing') processing++;
    }
    const allDone = completed === total;
    const hasProcessing = processing > 0;

    return (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={cn(
                    "w-full flex items-center justify-between px-5 py-4 text-left transition-colors duration-200",
                    "hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-inset",
                    isOpen && "border-b border-slate-100"
                )}
            >
                <div className="flex items-center gap-3 min-w-0">
                    <span className={cn(
                        "flex items-center justify-center w-8 h-8 rounded-lg text-sm font-bold shrink-0",
                        allDone
                            ? "bg-success-100 text-success-700"
                            : hasProcessing
                                ? "bg-primary-100 text-primary-700"
                                : "bg-slate-100 text-slate-600"
                    )}>
                        {index + 1}
                    </span>
                    <h3 className="text-base font-semibold text-slate-800 truncate">{title}</h3>
                </div>

                <div className="flex items-center gap-3 shrink-0 ml-3">
                    {/* Completion indicator */}
                    <div className="flex items-center gap-1.5">
                        {allDone ? (
                            <CheckCircle className="h-4 w-4 text-success-500" />
                        ) : hasProcessing ? (
                            <Loader className="h-4 w-4 text-primary-500 animate-spin" />
                        ) : (
                            <HelpCircle className="h-4 w-4 text-slate-300" />
                        )}
                        <span className={cn(
                            "text-xs font-medium tabular-nums",
                            allDone ? "text-success-600" : "text-slate-500"
                        )}>
                            {completed}/{total}
                        </span>
                    </div>

                    {/* Progress bar */}
                    <div className="w-16 h-1.5 rounded-full bg-slate-100 overflow-hidden hidden sm:block">
                        <div
                            className={cn(
                                "h-full rounded-full transition-all duration-500",
                                allDone ? "bg-success-500" : hasProcessing ? "bg-primary-500" : "bg-slate-300"
                            )}
                            style={{ width: `${total > 0 ? (completed / total) * 100 : 0}%` }}
                        />
                    </div>

                    {/* Chevron */}
                    {isOpen ? (
                        <ChevronDown className="h-5 w-5 text-slate-400 transition-transform duration-200" />
                    ) : (
                        <ChevronRight className="h-5 w-5 text-slate-400 transition-transform duration-200" />
                    )}
                </div>
            </button>

            <div
                className={cn(
                    "transition-all duration-300 ease-in-out overflow-hidden",
                    isOpen ? "max-h-[20000px] opacity-100" : "max-h-0 opacity-0"
                )}
            >
                <div className="p-4 space-y-4 bg-slate-50/50">
                    {children}
                </div>
            </div>
        </div>
    );
};
